import { compact, get, isArray, map } from 'lodash'
import { AxiosInstance } from 'axios'
import { FileWithIdentifier, Upload, UploadFileParam } from './types'
import { ApiService } from '../api'

/**
 * Deserialize a single upload or an array of uploads.
 *
 * @param {Upload[] | Upload} [uploads] - The upload(s) to deserialize.
 */
export const deserializeUpload = (uploads?: Upload[] | Upload): Upload[] => {
  const arrayUploads = compact(isArray(uploads) ? uploads : [uploads])
  return map(arrayUploads || [], (upload) => {
    return {
      ...upload,
    }
  })
}

/**
 * Format the JSON body for an upload request.
 *
 * @param {Object} options - The options for the JSON body.
 * @param {string} options.linkedEntityId - The linked entity ID.
 * @param {string} options.linkedEntityType - The linked entity type.
 * @param {Record<string, any>} [options.additionalInfo] - Additional information.
 * @param {File} options.file - The file to be uploaded.
 * @param {string[]} [options.tags] - Tags for the upload.
 */
export const formatJSONBody = ({
  linkedEntityId,
  linkedEntityType,
  additionalInfo = {},
  file,
  tags = [],
}: {
  linkedEntityId: string
  linkedEntityType: string
  additionalInfo?: Record<string, any>
  file: File
  tags?: string[]
}) =>
  JSON.stringify({
    linkedEntity: {
      linkedEntityId,
      linkedEntityType,
    },
    additionalInfo: {
      ...additionalInfo,
      ...getAdditionalInfo(file),
    },
    tags,
  })

/**
 * Get the name of a file without its extension.
 *
 * @param {File} file - The file to get the name of.
 */
export const getFileName = (file: File) => {
  const extension = get(file, 'name', '').split('.').pop()
  return get(file, 'name', '').substring(0, get(file, 'name', '').length - (extension?.length || 0) - 1)
}

/**
 * Get the format (extension) of a file.
 *
 * @param {File} file - The file to get the format of.
 */
export const getFileFormat = (file: File) => {
  return get(file, 'name', '').split('.').pop()
}

/**
 * Get additional information about a file.
 *
 * @param file - The file to get additional information about.
 */
export const getAdditionalInfo = (file: File) => {
  return {
    name: getFileName(file),
    format: getFileFormat(file),
    fullName: file?.name ?? '',
    size: file?.size ?? 0,
  }
}

/**
 * Convert a data URI to a File object.
 *
 * @param dataURI - The data URI to convert.
 * @param filename - The name of the file.
 */
export const dataURItoFile = (dataURI: string, filename: string) => {
  let byteString
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1])
  } else {
    byteString = unescape(dataURI.split(',')[1])
  }

  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }

  const blob = new Blob([ia], { type: mimeString })

  return new File([blob], filename, { type: mimeString })
}

/**
 * Map an array of FileWithIdentifier objects to an array of File objects.
 *
 * @param filesWithIdentifier - The array of FileWithIdentifier objects.
 */
export const mapFilesWithIdentifierToFiles = (filesWithIdentifier: FileWithIdentifier[]) => {
  return filesWithIdentifier.map((fileWithIdentifier) => fileWithIdentifier.file)
}

/**
 * Service for handling file uploads.
 */
export class UploadService {
  private readonly axios: AxiosInstance
  private readonly apiService: ApiService
  private readonly baseUrl = 'uploads'

  /**
   * Creates an instance of UploadService.
   *
   * @param instance - The Axios instance to use.
   */
  constructor(instance: AxiosInstance) {
    if (!instance) throw new Error('Instance is required')
    this.axios = instance
    this.apiService = new ApiService(instance)
  }

  /**
   * Delete an upload.
   *
   * @param upload - The upload to delete.
   */
  public deleteUpload = async (upload: Upload) => {
    return await this.apiService.deleteItem(this.baseUrl, upload._id)
  }

  /**
   * Delete multiple uploads.
   *
   * @param uploads - The uploads to delete.
   */
  public deleteUploads = async (uploads: Upload[]) => {
    return await Promise.all(uploads.map(this.deleteUpload))
  }

  /**
   * Upload a single file.
   *
   * @param options - The options for the upload.
   * @param options.file - The file to upload.
   * @param options.uploadFileParam - Parameters for the upload.
   */
  public uploadFile = async ({ file, uploadFileParam }: { file: File; uploadFileParam: UploadFileParam }) => {
    const { linkedEntityId, linkedEntityType, additionalInfo, url, axiosConfig } = uploadFileParam
    const uploadUrl = url || this.baseUrl
    const formData = new FormData()
    formData.append('file', file)
    formData.append(
      'JSONbody',
      formatJSONBody({
        linkedEntityId,
        linkedEntityType,
        additionalInfo,
        file,
        tags: [],
      })
    )

    return await this.apiService.createUpload<Upload>(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /**
   * Upload multiple files.
   *
   * @param files - The files to upload.
   * @param uploadFileParams - Parameters for the uploads.
   */
  public uploadFiles = async (files: File[], uploadFileParams: UploadFileParam): Promise<Upload[]> => {
    return await Promise.all(
      files.map((file) =>
        this.uploadFile({
          file: file,
          uploadFileParam: uploadFileParams,
        })
      )
    )
  }

  /**
   * Upload multiple files with identifiers.
   *
   * @param files - The files with identifiers to upload.
   * @param uploadFileParams - Parameters for the uploads.
   */
  public uploadFilesWithIdentifiers = async (files: FileWithIdentifier[], uploadFileParams: UploadFileParam) => {
    return await this.uploadFiles(mapFilesWithIdentifierToFiles(files), uploadFileParams)
  }

  /**
   * Upsert files.
   *
   * @param files - The files to upsert.
   * @param uploadsToRemove - The uploads to remove before upserting.
   * @param uploadFileParams - Parameters for the upserts.
   */
  public upsertFiles = async (files: File[], uploadsToRemove: Upload[], uploadFileParams: UploadFileParam) => {
    await this.deleteUploads(uploadsToRemove)
    return await this.uploadFiles(files, uploadFileParams)
  }

  /**
   * Upsert files with identifiers.
   *
   * @param filesWithIdentifier - The files with identifiers to upsert.
   * @param uploadsToRemove - The uploads to remove before upserting.
   * @param uploadFileParams - Parameters for the upserts.
   */
  public upsertFilesWithIdentifier = async (
    filesWithIdentifier: FileWithIdentifier[],
    uploadsToRemove: Upload[],
    uploadFileParams: UploadFileParam
  ) => {
    return await this.upsertFiles(mapFilesWithIdentifierToFiles(filesWithIdentifier), uploadsToRemove, uploadFileParams)
  }

  /**
   * Upload a base64 encoded file.
   *
   * @param options - The options for the upload.
   * @param options.base64file - The base64 encoded file data.
   * @param options.uploadFileParam - Parameters for the upload.
   * @param options.fileName - The name of the file.
   */
  public uploadBase64 = async ({
    base64file,
    uploadFileParam,
    fileName,
  }: {
    base64file: string
    uploadFileParam: UploadFileParam
    fileName: string
  }) => {
    const file = dataURItoFile(base64file, fileName)
    const formData = new FormData()
    formData.append('file', file)
    formData.append(
      'JSONbody',
      formatJSONBody({
        linkedEntityId: uploadFileParam.linkedEntityId,
        linkedEntityType: uploadFileParam.linkedEntityType,
        additionalInfo: uploadFileParam.additionalInfo || {},
        file,
        tags: [],
      })
    )
    return await this.apiService.createUpload('uploads', formData)
  }
}
